import { labelsMatch, normalizeLabel, stripTableQualifier } from './labelMatching'
import type { DashboardWidget } from './types'

type SortableColumn = {
  label: string
  sortKey: string
}

export function resolveWidgetSortKey(widget: DashboardWidget, requestedColumn: string | null | undefined) {
  if (!requestedColumn) return null

  const dimensionMatch = (widget.dimensions ?? []).find((dimension) => (
    labelsMatch(requestedColumn, dimension?.value)
    || labelsMatch(requestedColumn, dimension?.name)
    || labelsMatch(requestedColumn, stripTableQualifier(dimension?.value))
  ))
  if (dimensionMatch?.value) {
    return dimensionMatch.value
  }

  const measureMatch = (widget.measures ?? []).find((measure) => (
    labelsMatch(requestedColumn, measure?.value)
    || labelsMatch(requestedColumn, measure?.name)
    || labelsMatch(requestedColumn, stripTableQualifier(measure?.value))
  ))
  if (measureMatch?.value) {
    return measureMatch.value
  }

  return requestedColumn
}

export function buildWidgetSortOptions(widget: DashboardWidget, columns: string[]): SortableColumn[] {
  const seen = new Set<string>()

  return columns.flatMap((column) => {
    const sortKey = resolveWidgetSortKey(widget, column)
    if (!sortKey || seen.has(sortKey)) return []
    seen.add(sortKey)
    return [{ label: column, sortKey }]
  })
}

function looksLikeDateDimension(value: string | null | undefined) {
  const normalized = normalizeLabel(value)
  return normalized.includes('date')
    || normalized.includes('month')
    || normalized.includes('week')
    || normalized.includes('year')
    || normalized.includes('period')
}

export function resolveLineChartDateSortKey(widget: DashboardWidget) {
  if (widget.type !== 'line') return null

  const dimensions = widget.dimensions ?? []
  const preferredDimension = dimensions.find((dimension) => (
    looksLikeDateDimension(dimension.type)
    || looksLikeDateDimension(dimension.name)
    || looksLikeDateDimension(dimension.value)
  ))

  return preferredDimension?.value
    ?? widget.rowDimension
    ?? widget.daxGenerationRequest?.dimensions?.[0]
    ?? dimensions[0]?.value
    ?? null
}
